'use client';

import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useCurrentTime } from '@shared/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLotteryNextDraw } from '../../lotteries/hooks';
import type { LotteryName } from '../../lottery-purchases/types';
import type { NextDrawItem } from '../components/NextDraw/types';
import type { YourNumbersTicket } from '../components/YourNumbersSection/YourNumbersSection';
import { EMPTY_DRAWING_TICKETS, isAwaitingDrawingEvaluation, isDrawingTicketsLoading, useDrawingTickets } from '../hooks/useDrawingTickets/useDrawingTickets';
import type { Drawing, LotteryLiveDrawResponse } from '../types';
import { CLOCK_CHANGE_MS, getPreviousDrawingId, inferSkippedUpcomingDraw } from '../utils/getPickerDrawings';
import { ANNOUNCE_WIN_LEAD_MS, COUNTDOWN_GO_MS, PREVIOUS_RESULT_THRESHOLD_MS } from './constants';
import { getLiveDrawBallsEndMs } from './getLiveDrawRevealTiming';
import { getDrawnNumberCount, getRevealedCountForStage, getRevealedNumbers, type DrawnNumbers, type DrawnNumbersSource } from './getRevealedNumbers';
import { resolveLiveDrawStage, type LiveDrawStage } from './resolveLiveDrawStage';

/** Ball reveals land on 1.5s boundaries, so a 1s clock would visibly stutter against them. */
const LIVE_DRAW_TICK_MS = 250;

/**
 * Everywhere else a 1s clock is enough. `CurrentTimeEmitter` runs a single shared interval at the
 * fastest period any subscriber asks for, so holding the reveal clock all day would quadruple the
 * whole app's tick rate for the ~22h the tab spends on a stage that never moves.
 */
const IDLE_TICK_MS = 1_000;

/** Armed just before the draw so the 3/2/1/Go never starts a beat late, released once every stage has run. */
const FINE_CLOCK_LEAD_MS = 5 * 1_000;

/** Covers the reveal plus a lagging prize evaluation, so the win board still ticks at 250ms. */
const FINE_CLOCK_TAIL_MS = 3 * 60 * 1_000;

/** Drawn numbers only appear once the draw has run, so poll hard over that window. */
const AWAITING_RESULTS_POLL_MS = 2_000;

/** Otherwise just often enough to pick up the roll to the next drawing. */
const IDLE_POLL_MS = 60_000;

/** A replayed drawing closed its sales long ago, so the replay drops straight into the sequence. */
const REPLAY_MS_UNTIL_CLOSE = -1;

/** The drawing a replay plays back — the results-dropdown selection, or the live payload itself. */
type LiveDrawReplaySource = Pick<Drawing, 'drawingId' | 'drawingDate' | 'numbers' | 'extraNumbers' | 'drawnNumbers' | 'drawnExtraNumbers'>;

/**
 * Whatever drawing is on the board, whichever source it came from.
 *
 * `drawnNumbers` and `drawnExtraNumbers` come from the results list. The live payload has no
 * draw-order fields, so they stay absent there and the replay falls back to `numbers`.
 */
type BoardDrawing = DrawnNumbersSource & {
  drawingId: string;
  drawingDate?: string;
  drawnNumbers?: readonly unknown[] | null;
  drawnExtraNumbers?: readonly unknown[] | null;
};

type ArmedReplay = BoardDrawing & {
  anchorMs: number;
  /**
   * The live drawing whose sequence was running when this was armed, which only a date picked part
   * way through can do. Any other replay gives way the moment a live sequence starts.
   */
  liveDrawingId: string | undefined;
  /**
   * The player chose this drawing from the dropdown. A finished playback stays on the board through
   * the selling countdown, so they can replay it (WEBD-1516).
   */
  picked: boolean;
};

type UseLiveDrawStageOptions = Readonly<{
  /**
   * The results-dropdown drawing. Played back when the player arrives outside a live sequence
   * (WEBD-918 "Draw replay") and when they pick it with {@link UseLiveDrawStageResult.selectDrawing},
   * and what the Results tab loads tickets for. A change on its own never arms a replay: the
   * selection can land or swap for reasons other than the player. Omit to leave the tab resting on
   * its terminal stage instead.
   */
  replaySource?: LiveDrawReplaySource | null;
  /**
   * The results list. Its cadence places the drawing between the live payload and next-draw that
   * neither reports, so a page first loaded in that gap can still follow it. Omit to rest on the
   * payload there instead.
   */
  drawings?: readonly Pick<Drawing, 'drawingId' | 'drawingDate'>[] | null;
  /**
   * Whether the Live Draw header tab is selected. Tickets follow the board while it is, and the
   * dropdown selection on the Results tab. Defaults to true.
   */
  isLiveDrawView?: boolean;
  /**
   * Fired for each live drawing when the reveal ends (`calculating_win` or `complete`), and again
   * on `complete` after a calculating hold so wallet refetches run once the prize is known.
   * BRD FR-RP-05 withholds the visible balance until then. A player who picked another date part
   * way through still gets one firing once the live balls are out. Replays never fire it — nothing
   * has been won since last time.
   */
  onSequenceComplete?: () => void;
}>;

type UseLiveDrawStageResult = Readonly<{
  stage: LiveDrawStage;
  liveDraw: LotteryLiveDrawResponse | null;
  /**
   * The full set the current sequence is animating — live payload, or the armed replay source.
   * Null until that source has numbers (or extras) to show.
   */
  drawnNumbers: DrawnNumbers | null;
  /** How many of {@link drawnNumbers} have landed; 0 before the reveal and the full count once it has finished. */
  revealedCount: number;
  /**
   * Prefix of {@link drawnNumbers} that may be shown or matched. This is what Your Numbers and the
   * ball grid must use — the API returns the whole set at once.
   */
  revealedNumbers: DrawnNumbers;
  /** True while the stages are a replay of a past drawing rather than the live draw. */
  isReplay: boolean;
  /**
   * Whether the player-facing “Replay draw” control should show. A replay on the board keeps it
   * through its own playback, so the player can restart at any stage. Hidden while a live sequence
   * is animating (even under a replay picked part way through), on the selling countdown, and while
   * the payload is still loading.
   */
  canStartReplay: boolean;
  /** Re-arms a replay from the beginning. No-ops while an upcoming live sequence owns the tab. */
  startReplay: () => void;
  /**
   * The player picked `drawingDate` from the results dropdown. Plays that drawing back once it is
   * the `replaySource`, live sequence or not. Picking the live drawing, or the date the page opened
   * on, returns to the live sequence only while one is running or was watched here.
   */
  selectDrawing: (drawingDate: string) => void;
  /**
   * The board has painted. A replay or a joined 3/2/1/Go armed before that — while the animation
   * chunk was still loading — is moved to this moment so the clip opens on 3.
   */
  noteBoardPainted: () => void;
  /** Feeds the header countdown; see `stage.countdownTarget` for which one to show. */
  msUntilClose: number;
  msUntilDraw: number;
  /**
   * How far the current sequence has run, measured from its own start.
   *
   * The animated stages need this rather than {@link msUntilDraw}: that one tracks the real
   * drawing instant, so it is `Infinity` until the live payload loads and points at the original
   * drawing — hours or days ago — while a replay is running. Both cases would leave the
   * animations pinned at zero.
   */
  sequenceElapsedMs: number;
  /**
   * Sequence elapsed time at which the win board started, or the end of the reveal when the prize
   * was already known. Drives `AnnounceWinPanel` so a late evaluation does not seek the animation
   * past its last frame.
   */
  announceWinStartElapsedMs: number;
  /**
   * Caption for the live-draw card. Replay uses the archive drawing's date; the live path uses the
   * payload's date so a playback of yesterday is not labelled with today's 18:35.
   */
  drawingDate: string | undefined;
  isLoading: boolean;
  /**
   * The player's tickets for the drawing on screen, shaped for `YourNumbersSection`: the board's on
   * the Live Draw tab, the dropdown's on the Results tab.
   */
  tickets: YourNumbersTicket[];
  isTicketsLoading: boolean;
  /**
   * The player's numbers for this drawing, deduplicated, with main and extra kept apart so each
   * is matched against its own pool. Drives the "Match!" variant of the revealing ball.
   */
  playerNumbers: DrawnNumbers;
  /**
   * Total prize across the player's lines for this drawing, in cents, or `undefined` while it is
   * unknown. The win stage needs the figure to render.
   */
  totalWinnings: number | undefined;
}>;

function getLiveDrawPath(lotteryName: LotteryName): string {
  return API_ROUTES.LOTTERIES.LOTTERY_LIVE_DRAW.replace(':lotteryName', lotteryName);
}

/**
 * Hydration can still be showing the server clock when a sequence is armed. A client clock a
 * short way ahead of that snapshot opens 3/2/1/Go on 2, 1, or Go. Correct that skew onto the
 * client clock. Only a gap under a minute counts: a mocked test clock is much further from
 * `Date.now()`.
 */
const CLOCK_SKEW_MAX_MS = 60_000;

function sequenceAnchorMs(renderedNowMs: number): number {
  const clientNowMs = Date.now();
  const skewMs = clientNowMs - renderedNowMs;

  if (skewMs > LIVE_DRAW_TICK_MS && skewMs < CLOCK_SKEW_MAX_MS) {
    return clientNowMs;
  }

  return renderedNowMs;
}

/** Only the countdown and ball reveal need sub-second resolution; see {@link IDLE_TICK_MS}. */
function resolveTickMs(sequenceStartMs: number | null, nowMs: number): number {
  if (sequenceStartMs === null) {
    return IDLE_TICK_MS;
  }

  const isSequenceRunning = nowMs >= sequenceStartMs - FINE_CLOCK_LEAD_MS && nowMs <= sequenceStartMs + FINE_CLOCK_TAIL_MS;

  return isSequenceRunning ? LIVE_DRAW_TICK_MS : IDLE_TICK_MS;
}

function parseIsoToMs(value: string | undefined): number | null {
  if (!value) return null;

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** The live payload as a drawing on the board, its `additionalNumbers` read as the extras every other drawing carries. */
function toBoardDrawing(liveDraw: LotteryLiveDrawResponse | null): BoardDrawing | null {
  if (!liveDraw) {
    return null;
  }

  const { drawingId, drawingDate, numbers, additionalNumbers } = liveDraw;
  return { drawingId, drawingDate, numbers, ...(additionalNumbers?.length ? { extraNumbers: additionalNumbers } : {}) };
}

/** The drawing still on sale, from the next-draw endpoint; the live-draw one only reports it once it closes. */
type UpcomingDraw = { drawingId: string; closingMs: number; drawingMs: number };

function toUpcomingDraw(nextDraw: NextDrawItem | undefined): UpcomingDraw | null {
  const closingMs = nextDraw?.closingDate.regular;
  const drawingMs = nextDraw?.drawingDate;
  const drawingId = nextDraw?.id.regular;

  return closingMs == null || drawingMs == null || !drawingId ? null : { drawingId, closingMs, drawingMs };
}

/**
 * Whether the upcoming drawing closes after the one the payload reports. Compared on closing time
 * rather than id, which the two endpoints need not share.
 */
function isLaterThanPayload(upcoming: UpcomingDraw | null, payload: LotteryLiveDrawResponse | null | undefined): upcoming is UpcomingDraw {
  const payloadClosingMs = parseIsoToMs(payload?.closingDate);
  return upcoming !== null && (payloadClosingMs === null || upcoming.closingMs > payloadClosingMs);
}

/**
 * The drawing between the payload and next-draw that neither reports, placed by the results list's
 * own cadence — no schedule of the FE's. `null` when the cadence puts nothing between the two.
 *
 * Next-draw moves on to the following drawing the moment sales close, while the payload stays on the
 * previous one until the draw, so a page first loaded in between would otherwise have no source for
 * the drawing about to run. Its id is the one before next-draw's on the counter the endpoints share;
 * where they do not, it is empty and Your Numbers waits for the payload to name the drawing.
 *
 * The cadence runs in UTC while the draw keeps its local time, so a clock change since the newest
 * result puts the guess an hour out. Within that hour of either endpoint's drawing it is that
 * drawing, with nothing in between. Otherwise the guess is kept only while `nowMs` sits in its
 * close-to-draw window — next-draw moves on at the close, and the payload rolls on at the draw.
 * A nearer hour outside that window is a draw that does not exist, so the payload stays in charge.
 */
function inferClosedUndrawn(
  drawings: readonly Pick<Drawing, 'drawingId' | 'drawingDate'>[] | null | undefined,
  fetched: UpcomingDraw | null,
  payload: LotteryLiveDrawResponse | null,
  nowMs: number
): UpcomingDraw | null {
  if (!drawings?.length || fetched === null || payload === null) {
    return null;
  }

  const inferred = inferSkippedUpcomingDraw([...drawings], { nextDrawingDate: fetched.drawingMs, nextClosingDate: fetched.closingMs });
  const payloadClosingMs = parseIsoToMs(payload.closingDate);
  if (!inferred || inferred.closingDate >= fetched.closingMs - CLOCK_CHANGE_MS || (payloadClosingMs !== null && inferred.closingDate <= payloadClosingMs + CLOCK_CHANGE_MS)) {
    return null;
  }

  const isInCloseToDrawGap = (offsetMs: number) => {
    const closingMs = inferred.closingDate + offsetMs;
    const drawingMs = inferred.drawingDate + offsetMs;

    return closingMs <= nowMs && nowMs < drawingMs;
  };
  const offsetMs = [0, -CLOCK_CHANGE_MS, CLOCK_CHANGE_MS].find(isInCloseToDrawGap);
  if (offsetMs === undefined) {
    return null;
  }

  return {
    drawingId: getPreviousDrawingId(fetched.drawingId) ?? '',
    closingMs: inferred.closingDate + offsetMs,
    drawingMs: inferred.drawingDate + offsetMs,
  };
}

/** Whichever closes first, so a drawing inferred for the gap is not lost behind a later one held. */
function earlierUpcoming(held: UpcomingDraw | null, inferred: UpcomingDraw | null): UpcomingDraw | null {
  if (held === null || inferred === null) {
    return held ?? inferred;
  }

  return inferred.closingMs < held.closingMs ? inferred : held;
}

/**
 * The drawing to follow once the live payload has run its course: the one already held (or inferred,
 * see {@link inferClosedUndrawn}) while the payload has not caught up with it, or else whatever
 * next-draw reports now.
 *
 * Next-draw moves on to the following drawing the moment sales close, while the payload stays on
 * the previous one until the draw: between the two, neither reports the drawing counting down.
 * Without the hold, a next-draw refetch in that gap dropped the tab back to yesterday's drawing.
 * Only a payload that loaded can lag behind: one that never did would pin the hold for good.
 */
function holdUpcoming(fetched: UpcomingDraw | null, held: UpcomingDraw | null, payload: LotteryLiveDrawResponse | null): UpcomingDraw | null {
  return payload !== null && isLaterThanPayload(held, payload) && (fetched === null || held.closingMs < fetched.closingMs) ? held : fetched;
}

/**
 * Whether the payload is stalled on an empty grid for a drawing the results list has already
 * resulted (WEBD-1485). The live-draw endpoint is served with `max-age=3600` upstream, so it can
 * report the closed drawing with no numbers long after the results list has them.
 *
 * That wait is not a live sequence: it must not own the board, block the replay control, or hold
 * the hard poll, or the tab sits on an empty grid with no way out until the payload catches up.
 * Only the *same* drawing counts — a results selection for any other one, typically yesterday's,
 * leaves the empty-grid wait exactly as it was.
 */
function isStalledOnResultedDrawing(liveDrawing: BoardDrawing | null, replaySource: LiveDrawReplaySource | null | undefined): boolean {
  if (liveDrawing === null || getDrawnNumberCount(liveDrawing) > 0 || replaySource?.drawingId !== liveDrawing.drawingId) {
    return false;
  }

  return getDrawnNumberCount(replaySource) > 0;
}

/**
 * Polls hard while the reveal is stalled on an empty grid waiting for results, and from the upcoming
 * drawing's close until the endpoint rolls on to it — it keeps reporting the previous drawing until
 * then. Backs off otherwise.
 */
function resolveRefetchInterval(payload: LotteryLiveDrawResponse | null | undefined, upcoming: UpcomingDraw | null, replaySource: LiveDrawReplaySource | null | undefined): number {
  const nowMs = Date.now();
  const drawingMs = parseIsoToMs(payload?.drawingDate);
  // Only this limb backs off: the rollover poll below still has a drawing to wait for.
  const isAwaitingResults = drawingMs !== null && drawingMs <= nowMs && payload?.numbers.length === 0 && !isStalledOnResultedDrawing(toBoardDrawing(payload ?? null), replaySource);
  const isAwaitingRollover = isLaterThanPayload(upcoming, payload) && nowMs >= upcoming.closingMs;

  return isAwaitingResults || isAwaitingRollover ? AWAITING_RESULTS_POLL_MS : IDLE_POLL_MS;
}

/**
 * Whether the tab follows the upcoming drawing rather than the payload: from its final 2h of selling
 * (and past its close, until the payload catches up), once the payload's own sequence has run its
 * course. A payload that failed to load has no sequence; one still loading has not been read yet.
 */
function shouldTrackUpcoming({
  payload,
  isLoading,
  upcoming,
  nowMs,
}: {
  payload: LotteryLiveDrawResponse | null;
  isLoading: boolean;
  upcoming: UpcomingDraw | null;
  nowMs: number;
}): boolean {
  if (!isLaterThanPayload(upcoming, payload) || nowMs < upcoming.closingMs - PREVIOUS_RESULT_THRESHOLD_MS || (payload === null && isLoading)) {
    return false;
  }

  const payloadDrawingMs = parseIsoToMs(payload?.drawingDate);
  return payloadDrawingMs === null || nowMs >= payloadDrawingMs + FINE_CLOCK_TAIL_MS;
}

/** The upcoming drawing shaped like the payload it becomes once its sales close. */
function toPayloadStandIn(lotteryName: LotteryName, upcoming: UpcomingDraw): LotteryLiveDrawResponse {
  return {
    lotteryId: lotteryName,
    drawingId: upcoming.drawingId,
    closingDate: new Date(upcoming.closingMs).toISOString(),
    drawingDate: new Date(upcoming.drawingMs).toISOString(),
    numbers: [],
    additionalNumbers: [],
  };
}

/**
 * Stages that occupy the board with an in-progress sequence: countdown, ball reveal, or win
 * announcement.
 */
function isAnimatingStage(stage: LiveDrawStage): boolean {
  const kind = stage.slot.kind;
  return kind === 'draw_starting' || kind === 'countdown_go' || kind === 'drawing_numbers' || kind === 'calculating_win' || kind === 'announce_win';
}

function isFinishedStage(stage: LiveDrawStage): boolean {
  return stage.slot.kind === 'complete' || stage.slot.kind === 'previous_result';
}

function armReplay(source: BoardDrawing, anchorMs: number, liveDrawingId?: string, picked = false): ArmedReplay {
  return {
    anchorMs,
    drawingId: source.drawingId,
    drawingDate: source.drawingDate,
    numbers: source.drawnNumbers ?? source.numbers,
    extraNumbers: source.drawnExtraNumbers ?? source.extraNumbers,
    liveDrawingId,
    picked,
  };
}

/** Resulted archive slips, and a finished drawing resting on the board, show their completed footer. */
function withCompletedDraw(tickets: YourNumbersTicket[], isDrawComplete: boolean): YourNumbersTicket[] {
  if (!isDrawComplete) {
    return tickets;
  }

  return tickets.map(ticket => ({ ...ticket, isDrawComplete: true }));
}

/**
 * The drawing on screen: a replay, else the live payload while it has a sequence of its own, else
 * the drawing just watched live (the results list can lag it after the payload rolls on), else the
 * dropdown selection.
 */
function resolveBoardDrawing({
  replay,
  liveDraw,
  watched,
  replaySource,
}: {
  replay: ArmedReplay | null;
  liveDraw: BoardDrawing | null;
  watched: BoardDrawing | null;
  replaySource: LiveDrawReplaySource | null | undefined;
}): BoardDrawing | undefined {
  if (replay !== null) {
    return replay;
  }

  if (liveDraw !== null) {
    return liveDraw;
  }

  if (watched !== null) {
    return watched;
  }

  return replaySource ?? undefined;
}

type AnnounceWinTracking = {
  drawingKey: string;
  /** True once the sequence has been observed at or after the last ball's clip. */
  ballsSettled: boolean;
  /**
   * Latched when the balls settle. True when a prize was already known, so the win board waits
   * out {@link ANNOUNCE_WIN_LEAD_MS}. False when it was still being scored — the board then
   * starts at the moment the prize lands, with no extra pause.
   */
  winFollowsImmediately: boolean;
  delayedStartMs: number | null;
};

function emptyAnnounceWinTracking(drawingKey: string): AnnounceWinTracking {
  return { drawingKey, ballsSettled: false, winFollowsImmediately: false, delayedStartMs: null };
}

/**
 * The win board starts one second after the last ball when the prize is already known.
 * If it was still pending once the balls settled, the board starts at the elapsed time the
 * prize first lands, so the evaluating spinner is not held for that second.
 */
function readAnnounceWinStartElapsedMs({
  drawingKey,
  hasWinningTicket,
  sequenceElapsedMs,
  ballsEndMs,
  tracking,
}: {
  drawingKey: string;
  hasWinningTicket: boolean;
  sequenceElapsedMs: number;
  ballsEndMs: number;
  tracking: AnnounceWinTracking;
}): { startMs: number; tracking: AnnounceWinTracking } {
  const immediateStartMs = ballsEndMs + ANNOUNCE_WIN_LEAD_MS;
  const previous = tracking.drawingKey === drawingKey ? tracking : emptyAnnounceWinTracking(drawingKey);

  if (sequenceElapsedMs < ballsEndMs) {
    const reset = emptyAnnounceWinTracking(drawingKey);
    const isAlreadyReset = !previous.ballsSettled && !previous.winFollowsImmediately && previous.delayedStartMs === null;

    return { startMs: immediateStartMs, tracking: isAlreadyReset ? previous : reset };
  }

  const winFollowsImmediately = previous.ballsSettled ? previous.winFollowsImmediately : hasWinningTicket;
  const delayedStartMs = !winFollowsImmediately && hasWinningTicket && previous.delayedStartMs === null ? sequenceElapsedMs : previous.delayedStartMs;
  const startMs = winFollowsImmediately ? immediateStartMs : (delayedStartMs ?? immediateStartMs);
  const next: AnnounceWinTracking = { drawingKey, ballsSettled: true, winFollowsImmediately, delayedStartMs };
  const isUnchanged =
    previous.ballsSettled === next.ballsSettled && previous.winFollowsImmediately === next.winFollowsImmediately && previous.delayedStartMs === next.delayedStartMs;

  return { startMs, tracking: isUnchanged ? previous : next };
}

/**
 * Runs the live-draw tab for a lottery: decides which drawing is on the board, loads the player's
 * tickets for it, and resolves the stage to render from wall-clock time.
 *
 * - A live sequence owns the board from sales close. It is derived from the payload's two
 *   dates, so landing mid-draw — reveal, calculating hold or win board — resolves to the stage
 *   already in progress rather than restarting (WEBD-918; BRD FR-RP-05). Landing inside
 *   3/2/1/Go is the exception: that clip restarts from 3, then the reveal catches up to the
 *   drawing clock.
 * - The live-draw endpoint reports the latest drawing whose sales have closed, and keeps reporting
 *   it until the next one closes. So from 2h before that close the tab follows the next drawing
 *   instead, from the next-draw endpoint: its selling countdown, then its "live draw in".
 * - Arriving outside one replays `replaySource` once, from the moment the player landed — the same
 *   stages, anchored on landing rather than on the draw.
 * - After that only the player arms a replay: the replay control, or picking a date through
 *   `selectDrawing`, which plays that drawing straight away, live sequence or not. Picking the live
 *   drawing, or the date the page opened on, returns to the live sequence — but only when one is
 *   running, or one was watched here. A finished drawing the player arrived too late for replays
 *   again when they pick it back.
 * - A drawing watched live here stays behind the default selection after the payload rolls over,
 *   so Your Numbers keeps its slip until the player picks something else.
 *
 * Tickets are fetched here, between choosing the drawing and resolving its stage, because each needs
 * the other: which tickets to load depends on the drawing, and the win stages depend on the tickets.
 * The choice itself only needs timing the payload already carries, so it never waits on them.
 *
 * Until the payload lands both offsets are infinite, which resolves to `previous_result` — the same
 * stage shown for most of the day, so there is no loading flash.
 */
function useLiveDrawStage(lotteryName: LotteryName, { replaySource, drawings, isLiveDrawView = true, onSequenceComplete }: UseLiveDrawStageOptions = {}): UseLiveDrawStageResult {
  const { data: nextDraws } = useLotteryNextDraw(lotteryName);
  // Memoised on the query data, which React Query keeps stable, so the stand-in below is too.
  const fetchedUpcoming = useMemo(() => toUpcomingDraw(nextDraws?.find(draw => draw.lotteryName === lotteryName)), [lotteryName, nextDraws]);
  const [heldUpcoming, setHeldUpcoming] = useState<UpcomingDraw | null>(null);
  // Coarse pass first, so the fine clock is only subscribed to while a sequence is actually animating.
  const coarseNowMs = useCurrentTime({ updatePeriod: IDLE_TICK_MS, resolution: IDLE_TICK_MS });

  const { data: payload = null, isLoading } = useApiQuery<LotteryLiveDrawResponse>(getLiveDrawPath(lotteryName), {
    // The endpoint is served with `max-age=3600`, so without this the browser answers the polls from
    // its cache and the numbers can miss the draw by up to an hour.
    cache: 'no-store',
    queryOptions: {
      refetchInterval: query => {
        const polledPayload = query.state.data ?? null;
        const followed = earlierUpcoming(heldUpcoming, inferClosedUndrawn(drawings, fetchedUpcoming, polledPayload, Date.now()));
        return resolveRefetchInterval(polledPayload, holdUpcoming(fetchedUpcoming, followed, polledPayload), replaySource);
      },
    },
  });

  const closedUndrawn = useMemo(() => inferClosedUndrawn(drawings, fetchedUpcoming, payload, coarseNowMs), [drawings, fetchedUpcoming, payload, coarseNowMs]);
  const upcoming = holdUpcoming(fetchedUpcoming, earlierUpcoming(heldUpcoming, closedUndrawn), payload);
  if (upcoming !== heldUpcoming) {
    setHeldUpcoming(upcoming);
  }

  const upcomingStandIn = useMemo(() => (upcoming === null ? null : toPayloadStandIn(lotteryName, upcoming)), [lotteryName, upcoming]);
  // The drawing the tab follows; everything below reads it as the live payload.
  const liveDraw = shouldTrackUpcoming({ payload, isLoading, upcoming, nowMs: coarseNowMs }) ? upcomingStandIn : payload;

  const closingMs = useMemo(() => parseIsoToMs(liveDraw?.closingDate), [liveDraw?.closingDate]);
  const drawingMs = useMemo(() => parseIsoToMs(liveDraw?.drawingDate), [liveDraw?.drawingDate]);
  const liveDrawing = useMemo(() => toBoardDrawing(liveDraw), [liveDraw]);
  const payloadDrawing = useMemo(() => toBoardDrawing(payload), [payload]);

  const [replay, setReplay] = useState<ArmedReplay | null>(null);
  /** The live drawing whose sequence played on the board here. */
  const [watchedLive, setWatchedLive] = useState<BoardDrawing | null>(null);
  /** Whether the one landing replay has been decided; after it only the player arms a replay. */
  const [hasLanded, setHasLanded] = useState(false);
  /** Page-load instant, set only when the payload arrives during 3/2/1/Go. Null once that clip has finished. */
  const [countdownJoinAnchorMs, setCountdownJoinAnchorMs] = useState<number | null>(null);
  /** Decided once a drawing date is known, so a countdown watched from `draw_starting` is not rewound. */
  const [hasResolvedCountdownJoin, setHasResolvedCountdownJoin] = useState(false);
  /** Date the player picked, held until `replaySource` catches up with it. */
  const [pendingPick, setPendingPick] = useState<string | null>(null);
  /** Replays already lined up with the board, so a later paint cannot rewind them. */
  const paintedReplayAnchors = useRef(new Set<string>());
  const hasPaintedCountdownJoin = useRef(false);
  /** The first selection the page offered. Picking it back returns to a live sequence, when one is on. */
  const [defaultSelectionId, setDefaultSelectionId] = useState<string>();
  const [announceWinTracking, setAnnounceWinTracking] = useState(emptyAnnounceWinTracking(''));

  if (defaultSelectionId === undefined && replaySource?.drawingId) {
    setDefaultSelectionId(replaySource.drawingId);
  }

  const tickMs = resolveTickMs(replay?.anchorMs ?? drawingMs, coarseNowMs);
  const nowMs = useCurrentTime({ updatePeriod: tickMs, resolution: tickMs });

  const msUntilClose = closingMs === null ? Number.POSITIVE_INFINITY : closingMs - nowMs;
  const msUntilDraw = drawingMs === null ? Number.POSITIVE_INFINITY : drawingMs - nowMs;
  const liveElapsedMs = Number.isFinite(msUntilDraw) ? Math.max(0, -msUntilDraw) : 0;
  const liveNumberCount = getDrawnNumberCount(liveDrawing);

  // Where the live payload is, from its timing alone. Win status only matters once the balls are
  // out, so choosing the drawing never has to wait on the tickets for it.
  const isLiveInWindow = msUntilClose <= 0;

  const countdownJoinElapsedMs = countdownJoinAnchorMs === null ? null : Math.max(0, nowMs - countdownJoinAnchorMs);
  const countdownJoinExpired = countdownJoinElapsedMs !== null && countdownJoinElapsedMs >= COUNTDOWN_GO_MS;

  if (countdownJoinExpired) {
    setCountdownJoinAnchorMs(null);
  }

  const isPlayingJoinedCountdown = countdownJoinElapsedMs !== null && !countdownJoinExpired && replay === null;
  const isSellingWindow = msUntilClose > 0 && msUntilClose <= PREVIOUS_RESULT_THRESHOLD_MS;
  const haveLiveBallsLanded = liveNumberCount > 0 && liveElapsedMs >= getLiveDrawBallsEndMs(liveNumberCount);
  // A payload the results list has already overtaken has no sequence of its own to animate (WEBD-1485).
  const isStalledOnResult = isStalledOnResultedDrawing(liveDrawing, replaySource);
  const isLiveAnimating = isLiveInWindow && !haveLiveBallsLanded && !isStalledOnResult;

  const isOnDefaultSelection = replaySource?.drawingId !== undefined && replaySource.drawingId === defaultSelectionId;

  const boardDrawing = resolveBoardDrawing({
    replay,
    liveDraw: isLiveInWindow ? liveDrawing : null,
    watched: isOnDefaultSelection ? watchedLive : null,
    replaySource,
  });
  // The Results tab is the archive: it shows the dropdown drawing whatever the board is playing.
  // Win status always comes from the board's own drawing — the live payload's tickets for the live
  // stages, the replay's for a replay — so a winning archive slip on the Results tab cannot put the
  // live sequence on its win board. Both hooks share one query when they name the same drawing.
  const hasBoardNumbers = getDrawnNumberCount(boardDrawing) > 0;
  const boardDrawingId = boardDrawing?.drawingId || undefined;
  const archiveDrawingId = isLiveDrawView ? undefined : replaySource?.drawingId || undefined;
  const boardTickets = useDrawingTickets({ lotteryName, drawingId: boardDrawingId, hasDrawnNumbers: hasBoardNumbers, drawnNumbers: boardDrawing });
  // The Results tab is the archive: its slip is the dropdown drawing whatever the board is playing.
  const archiveTickets = useDrawingTickets({
    lotteryName,
    drawingId: archiveDrawingId,
    hasDrawnNumbers: getDrawnNumberCount(replaySource) > 0,
    drawnNumbers: replaySource ?? undefined,
  });
  const shownTickets = isLiveDrawView ? boardTickets : archiveTickets;
  const { isLoading: isSessionLoading } = useSession();
  const boardView = boardTickets.data ?? EMPTY_DRAWING_TICKETS;
  const shownView = shownTickets.data ?? EMPTY_DRAWING_TICKETS;
  const { totalWinnings } = boardView;
  const isAwaitingEvaluation = isAwaitingDrawingEvaluation(boardTickets, { hasDrawing: boardDrawingId !== undefined, hasDrawnNumbers: hasBoardNumbers, isSessionLoading });
  const hasWinningTicket = totalWinnings !== undefined && totalWinnings > 0;

  // Armed replay carries its own numbers so a live-payload preference survives a lagging results list.
  // Without one this is the live payload, which is also the board drawing whenever the stage shows
  // balls: those stages need sales closed, which keeps the payload on the board. After it rolls over
  // the stage is `previous_result` or `selling_countdown`, which draw no balls.
  const numbersSource = replay ?? liveDrawing;
  const drawnNumberCount = getDrawnNumberCount(numbersSource);
  // Clamped because `nowMs` is floored to the tick resolution and can trail the anchor by one tick.
  const replayElapsedMs = replay === null ? null : Math.max(0, nowMs - replay.anchorMs);
  const joinedElapsedMs = isPlayingJoinedCountdown ? countdownJoinElapsedMs : null;
  const sequenceElapsedMs = replayElapsedMs ?? joinedElapsedMs ?? liveElapsedMs;
  const announceWinRead = readAnnounceWinStartElapsedMs({
    drawingKey: `${replay !== null ? 'replay' : 'live'}:${numbersSource?.drawingId ?? ''}`,
    hasWinningTicket,
    sequenceElapsedMs,
    ballsEndMs: getLiveDrawBallsEndMs(drawnNumberCount),
    tracking: announceWinTracking,
  });
  if (announceWinRead.tracking !== announceWinTracking) {
    setAnnounceWinTracking(announceWinRead.tracking);
  }
  const announceWinStartElapsedMs = announceWinRead.startMs;

  // The live win stages belong to a sequence the player was here for. The endpoint holds a drawing
  // until the next one closes — all day — so a player arriving after its tail sees the finished
  // board, rather than its calculating hold while tickets load and then You won! once they do.
  // Only the live board: a replay of that drawing, or any other, still plays its win in full.
  const wasPresentForLive = liveElapsedMs <= FINE_CLOCK_TAIL_MS || (watchedLive !== null && watchedLive.drawingId === liveDraw?.drawingId);
  const liveStage = resolveLiveDrawStage({
    msUntilClose,
    msUntilDraw,
    drawnNumberCount: liveNumberCount,
    hasWinningTicket: wasPresentForLive && hasWinningTicket,
    isAwaitingEvaluation: wasPresentForLive && isAwaitingEvaluation,
    announceWinStartElapsedMs,
  });
  const replayStage =
    replay === null
      ? null
      : resolveLiveDrawStage({
          msUntilClose: REPLAY_MS_UNTIL_CLOSE,
          msUntilDraw: -sequenceElapsedMs,
          drawnNumberCount,
          hasWinningTicket,
          // A past drawing's prize is already scored: the replay never holds on the calculating board.
          // Tickets still loading as the balls settle just mean You won! starts once they land.
          isAwaitingEvaluation: false,
          announceWinStartElapsedMs,
        });
  const isReplayFinished = replayStage !== null && isFinishedStage(replayStage);
  // A finished replay hands the board back to the selling countdown, unless the player picked that
  // drawing — they stay on its completed slip so the replay control can bring it back (WEBD-1516).
  const handsFinishedReplayBack = isSellingWindow && isReplayFinished && replay?.picked !== true;
  const isReplay = replayStage !== null && !handsFinishedReplayBack;
  const wallClockStage = isReplay ? replayStage : liveStage;
  const stage = isPlayingJoinedCountdown ? { slot: { kind: 'countdown_go' as const }, countdownTarget: null } : wallClockStage;
  const isAtRest = !isReplay && !isLiveInWindow;

  // The live sequence holds the board through its calculating hold and win board too. Those stages
  // need the live drawing's tickets, which it only has while no replay is on the board — true on
  // landing, which is when this matters.
  const isLiveOwningBoard = isLiveAnimating || (replay === null && isLiveInWindow && !isStalledOnResult && isAnimatingStage(liveStage));
  const liveDrawingId = liveDraw?.drawingId;

  // Resolved from an effect so the server render cannot bake its clock into the anchor. The
  // rendered `nowMs` can still be that snapshot; `sequenceAnchorMs` moves a short skew onto the client.
  useEffect(() => {
    if (hasResolvedCountdownJoin || isLoading || drawingMs === null) {
      return;
    }

    setHasResolvedCountdownJoin(true);

    if (replay === null && isLiveInWindow && msUntilDraw <= 0 && liveElapsedMs < COUNTDOWN_GO_MS) {
      setCountdownJoinAnchorMs(sequenceAnchorMs(nowMs));
    }
  }, [drawingMs, hasResolvedCountdownJoin, isLiveInWindow, isLoading, liveElapsedMs, msUntilDraw, nowMs, replay]);

  useEffect(() => {
    if (isLiveOwningBoard && liveDrawing !== null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- the live sequence reaching the board is only observable here.
      setWatchedLive(liveDrawing);
    }

    // Only a date picked part way through may hold the board against a live sequence.
    if (isLiveAnimating && replay !== null && replay.liveDrawingId !== liveDrawingId) {
      setReplay(null);
      return;
    }

    // Final 2h before close: a finished replay hands the tab back to the selling countdown (WEBD-949).
    // A date the player picked stays, so Monday's completed board is not replaced by Tuesday's promo.
    if (handsFinishedReplayBack) {
      setReplay(null);
      return;
    }

    if (pendingPick !== null) {
      if (replaySource?.drawingDate !== pendingPick) {
        return;
      }

      setPendingPick(null);

      // Yesterday stays on the endpoint until the next close, so hours before today's draw it is
      // both the live payload and the date the page opened on. That is a past result, not a live
      // sequence to return to — coming back to it plays it again (WEBD-1528).
      const hasLiveSequence = isLiveAnimating || isLiveOwningBoard || watchedLive !== null;
      const home = hasLiveSequence ? ((isLiveInWindow ? liveDrawing : null) ?? watchedLive) : null;
      const isBackHome = home !== null && (replaySource.drawingId === home.drawingId || replaySource.drawingId === defaultSelectionId);

      if (isBackHome || getDrawnNumberCount(replaySource) === 0) {
        setReplay(null);
        return;
      }

      // The anchor is the moment the player picked, which only an effect can observe.
      setReplay(armReplay(replaySource, sequenceAnchorMs(nowMs), isLiveAnimating ? liveDrawingId : undefined, true));
      return;
    }

    if (hasLanded || isLoading) {
      return;
    }

    // A live sequence already on the board, or the selling countdown, keeps the tab on landing.
    if (isLiveOwningBoard || isSellingWindow) {
      setHasLanded(true);
      return;
    }

    // Waiting on the selection rather than deciding without it, so a slow results list cannot be
    // mistaken for anything the player did.
    if (!replaySource) {
      return;
    }

    setHasLanded(true);

    if (getDrawnNumberCount(replaySource) > 0) {
      // The anchor is the moment the player landed, which only an effect can observe.
      // Claiming the stalled drawing keeps this replay when the payload catches up mid-sequence: it
      // is playing that very drawing's numbers, so preempting it would only jump the same reveal
      // forward to wherever the live clock had got to (WEBD-1485).
      setReplay(armReplay(replaySource, sequenceAnchorMs(nowMs), isStalledOnResult ? liveDrawingId : undefined));
    }
  }, [
    defaultSelectionId,
    hasLanded,
    isLiveAnimating,
    isLiveInWindow,
    handsFinishedReplayBack,
    isLiveOwningBoard,
    isLoading,
    isSellingWindow,
    isStalledOnResult,
    liveDrawing,
    liveDrawingId,
    nowMs,
    pendingPick,
    replay,
    replaySource,
    watchedLive,
  ]);

  const revealedCount = getRevealedCountForStage(stage, drawnNumberCount);
  const revealedNumbers = getRevealedNumbers(numbersSource, revealedCount);
  const drawnNumbers = drawnNumberCount === 0 ? null : getRevealedNumbers(numbersSource, drawnNumberCount);

  // Once per slot per drawing, so a calculating hold can refetch, and complete can refetch again once
  // the prize is known — and never back to calculating after complete. Each firing refetches the
  // tickets, and a refetch can bounce the stage between the two: signalling every bounce refetched
  // without end for a signed-out player, whose tickets never load. While a replay is on the board the
  // live stages are not being played, so only "balls out" is known for the live drawing.
  const signalledDrawingRef = useRef<{ drawingId: string; kinds: ReadonlySet<'calculating_win' | 'complete'> } | null>(null);
  const liveStageKind = replay === null ? liveStage.slot.kind : haveLiveBallsLanded ? 'complete' : null;
  const liveSignalKind = liveStageKind === 'complete' || liveStageKind === 'calculating_win' ? liveStageKind : null;

  useEffect(() => {
    if (liveDrawingId === undefined || liveSignalKind === null) {
      return;
    }

    const signalled = signalledDrawingRef.current?.drawingId === liveDrawingId ? signalledDrawingRef.current.kinds : new Set<'calculating_win' | 'complete'>();

    if (signalled.has(liveSignalKind) || (liveSignalKind === 'calculating_win' && signalled.has('complete'))) {
      return;
    }

    signalledDrawingRef.current = { drawingId: liveDrawingId, kinds: new Set([...signalled, liveSignalKind]) };
    onSequenceComplete?.();
  }, [liveDrawingId, liveSignalKind, onSequenceComplete]);

  // The live drawing a replay can play: the finished one the endpoint reports. After a live finish the
  // results list can still point at yesterday, so the replay prefers this payload over that selection.
  const payloadNumberCount = getDrawnNumberCount(payloadDrawing);
  const payloadDrawingMs = parseIsoToMs(payload?.drawingDate);
  const canReplayLivePayload = payloadNumberCount > 0 && payloadDrawingMs !== null && nowMs - payloadDrawingMs >= getLiveDrawBallsEndMs(payloadNumberCount);
  const hasReplayableSource = getDrawnNumberCount(replaySource) > 0 || canReplayLivePayload;
  // The selling countdown is the promo for today's upcoming draw. Replaying from it plays yesterday's
  // numbers under today's date (WEBD-1516). A past date picked from the dropdown replays, and stays
  // once that playback finishes.
  const isSellingCountdown = stage.slot.kind === 'selling_countdown';
  // A replay on the board stays restartable through its own playback (WEBD-1528). Waiting until the
  // animation finished made the button pop in and shove the content above it. A live sequence still
  // animating underneath one, after a date picked part way through, keeps it hidden.
  const canStartReplay = !isLoading && !isSellingCountdown && !isLiveAnimating && (isReplay || (hasReplayableSource && !isAnimatingStage(stage)));

  const startReplay = useCallback(() => {
    if (isLiveAnimating || isSellingCountdown) {
      return;
    }

    // Re-arm the drawing already on screen, including mid-playback, so a second press does not fall
    // back to a lagging results-list selection after we preferred the live payload on the first.
    if (isReplay && replay !== null) {
      setReplay({ ...replay, anchorMs: sequenceAnchorMs(nowMs) });
      return;
    }

    // Prefer the live payload when resting on a finished live sequence: `replaySource` can lag.
    const source = canReplayLivePayload ? payloadDrawing : replaySource;

    if (!source?.drawingId || getDrawnNumberCount(source) === 0) {
      return;
    }

    setReplay(armReplay(source, sequenceAnchorMs(nowMs)));
  }, [canReplayLivePayload, isLiveAnimating, isReplay, isSellingCountdown, nowMs, payloadDrawing, replay, replaySource]);

  const selectDrawing = useCallback((drawingDate: string) => {
    // A pick is the player's call, so it also settles the landing replay.
    setHasLanded(true);
    setPendingPick(drawingDate);
  }, []);

  // The landing effect arms the clock before the lazy board exists. Without this, 3/2/1/Go opens
  // on whatever frame the chunk's load time has already reached.
  const noteBoardPainted = useCallback(() => {
    setReplay(current => {
      if (current === null) {
        return current;
      }

      const key = `${current.drawingId}:${current.anchorMs}`;

      if (paintedReplayAnchors.current.has(key)) {
        return current;
      }

      paintedReplayAnchors.current.add(key);

      const anchorMs = Date.now();
      paintedReplayAnchors.current.add(`${current.drawingId}:${anchorMs}`);

      if (current.anchorMs === anchorMs) {
        return current;
      }

      return { ...current, anchorMs };
    });

    setCountdownJoinAnchorMs(current => {
      if (current === null || hasPaintedCountdownJoin.current) {
        return current;
      }

      hasPaintedCountdownJoin.current = true;

      return Date.now();
    });
  }, []);

  return {
    stage,
    liveDraw,
    drawnNumbers,
    revealedCount,
    revealedNumbers,
    isReplay,
    canStartReplay,
    startReplay,
    selectDrawing,
    noteBoardPainted,
    msUntilClose,
    msUntilDraw,
    sequenceElapsedMs,
    announceWinStartElapsedMs,
    drawingDate: isReplay ? replay?.drawingDate : liveDraw?.drawingDate,
    isLoading,
    // The Results tab skips the replay, but only once this drawing has balls. Before close the
    // placeholder has none, and forcing complete there shows "Total winnings: $0" instead of the luck message.
    tickets: withCompletedDraw(shownView.tickets, (!isLiveDrawView && getDrawnNumberCount(replaySource) > 0) || (isLiveDrawView && isAtRest && hasBoardNumbers)),
    isTicketsLoading: isDrawingTicketsLoading(shownTickets, { hasDrawing: (isLiveDrawView ? boardDrawingId : archiveDrawingId) !== undefined, isSessionLoading }),
    playerNumbers: boardView.playerNumbers,
    totalWinnings,
  };
}

export { getDrawnNumberCount, getLiveDrawPath, useLiveDrawStage };
export type { LiveDrawReplaySource, UseLiveDrawStageOptions, UseLiveDrawStageResult };
